'use client'

import { useRegisterDeliveryInformationForm } from '@features/delivery/model/hooks'
import {
  RegisterDeliveryInformationFormState,
  RegisterDeliveryInformationFormValues,
} from '@features/delivery/model/schemas'
import { getUserPodoInfoSchema } from '@server/schemas'
import { createContext, ReactNode, use, useContext } from 'react'
import { z } from 'zod'

type RegisterDeliveryInformationFormContextType = {
  formState: RegisterDeliveryInformationFormState
  validationNumeric: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleChange: (name: keyof RegisterDeliveryInformationFormValues, value: string) => void
  isFormValid: boolean
}

export const RegisterDeliveryInformationFormContext =
  createContext<Nullable<RegisterDeliveryInformationFormContextType>>(null)

export const RegisterDeliveryInformationFormProvider = ({
  children,
  initialValues,
  getCurrentUserPromise,
}: {
  children: ReactNode
  initialValues: Partial<RegisterDeliveryInformationFormValues>
  getCurrentUserPromise: Promise<z.infer<typeof getUserPodoInfoSchema>>
}) => {
  const userInfo = use(getCurrentUserPromise)
  const { formState, validationNumeric, handleChange, isFormValid } = useRegisterDeliveryInformationForm({
    ...initialValues,
    name: userInfo.name,
    phone: userInfo.phone,
  })

  return (
    <RegisterDeliveryInformationFormContext.Provider
      value={{ formState, validationNumeric, handleChange, isFormValid }}
    >
      {children}
    </RegisterDeliveryInformationFormContext.Provider>
  )
}

export const useRegisterDeliveryInformationFormState = () => {
  const context = useContext(RegisterDeliveryInformationFormContext)
  if (!context) {
    throw new Error(
      'useRegisterDeliveryInformationFormState must be used within a RegisterDeliveryInformationFormProvider',
    )
  }
  return context
}
